import { IUser } from "./user";

/**
 * accesscontrol-style actions. Each capability is modelled as one of the four
 * CRUD verbs against a named resource, with either "any" or "own" possession.
 */
export enum PermissionAction {
  CreateAny = "create:any",
  ReadAny = "read:any",
  UpdateAny = "update:any",
  DeleteAny = "delete:any",
  CreateOwn = "create:own",
  ReadOwn = "read:own",
  UpdateOwn = "update:own",
  DeleteOwn = "delete:own",
}

/**
 * The set of protected resources in the system. A capability is expressed as
 * an (action, resource) pair, e.g. UpdateAny + LiveTemplate = "put a template live".
 */
export enum PermissionResource {
  LiveTemplate = "live-template",
  AgenticWorkflow = "agentic-workflow",
  Template = "template",
  AffiliateProduct = "affiliate-product",
  Task = "task",
  CheckoutPlatform = "checkout-platform",
  KindredData = "kindred-data",
  FullAutoCouponsTargeting = "full-auto-coupons-targeting",
  TestTools = "test-tools",
  GroupMembership = "group-membership",
  BasketTesting = "basket-testing",
  QaList = "qa-list",
  WorkPipeline = "work-pipeline",
  Results = "results",
  Reporting = "reporting",
  Merchant = "merchant",
  UserGroup = "user-group",
  OpenData = "open-data",
  CouponBulkUpdate = "coupon-bulk-update",
  DomainReview = "domain-review",
  SmartShopping = "smart-shopping",
  Partners = "partners",
}

/**
 * Slugs for the predefined (system) groups. Custom groups created by admins use
 * their own generated slugs and are not part of this enum.
 */
export enum SystemGroup {
  Admins = "admins",
  Developers = "developers",
  QaManagers = "qa-managers",
  LeadQaTesters = "lead-qa-testers",
  QaTesters = "qa-testers",
  /** QA Tester access, working the Work Pipeline's Dev fixes bucket as devs. */
  TemplateDevelopers = "template-developers",
  Reporting = "reporting",
  /** Owns the live partner configuration (Partners panel). */
  PartnerManagers = "partner-managers",
}

/** A single permission entry attached to a group. */
export interface IPermission {
  resource: PermissionResource;
  action: PermissionAction;
  /** accesscontrol attribute filter; defaults to "*" (all fields). */
  attributes: string;
}

/** A group as listed in management views. */
export interface IGroup {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  /** System groups are predefined and protected from delete/rename. */
  isSystem: boolean;
  memberCount: number;
  /**
   * Whether the requesting user may add/remove members of this group.
   * Computed per-request; absent when not relevant.
   */
  canManageMembers?: boolean;
}

/** A group with its full membership and permission set. */
export interface IGroupDetailed extends IGroup {
  users: IUser[];
  permissions: IPermission[];
}

/** Payload to create a custom group. */
export interface ICreateGroupRequest {
  name: string;
  description?: string;
  permissions: IPermission[];
}

/** Payload to update a group (name/description and, for custom groups, permissions). */
export interface IUpdateGroupRequest {
  name?: string;
  description?: string;
  permissions?: IPermission[];
}

/** Payload to add/remove a user from a group. */
export interface IGroupMembershipRequest {
  userId: string;
}

/**
 * The current user's effective permissions, returned to the webapp so it can
 * gate UI. `isAdmin` short-circuits every check.
 */
export interface IUserPermissions {
  isAdmin: boolean;
  groups: string[];
  permissions: IPermission[];
}
